import { TraceHeaders } from './types';

export type RequestValue = string | number | boolean | string[];
type RequestRecord = { [key: string]: RequestValue };
export type ParamsObj = {
  [key: string]: RequestValue | Record<string, RequestValue>;
};

const FILTER_KEY = 'filter';

const transformFilterProp = (
  params: Record<string, RequestValue> = {},
  name: string = FILTER_KEY,
): string =>
  Object.keys(params).reduce((str, key) => {
    const strStart = str ? `${str}&` : str;
    return `${strStart}${name}=${key}(${params[key].toString()})`;
  }, '');

export const transformParamsToString = (params: ParamsObj = {}): string => {
  const entries = Object.entries(params);
  const keyValuePairs = entries
    .map(([key, value]) => {
      if (value === null || value === undefined) {
        return null;
      }
      // Used for filters in thread API as they are passed as object
      if (value instanceof Object && !(value instanceof Array)) {
        return transformFilterProp(value, key);
      }
      return `${key}=${value?.toString()}`;
    })
    .filter(a => !!a);

  return entries.length ? `?${keyValuePairs.join('&')}` : '';
};

export const renameFilterProps = (filters: RequestRecord) => {
  const MAPPER = {
    collections: 'publishedContent.properties.publish.collections',
    excludeCollectionIds:
      'excludePublishedContent.propertiesPublishCollections',
    pageType: 'publishedContent.properties.pageType',
    productIds: 'productInfo.merchProduct.id',
    styleColors: 'productInfo.merchProduct.styleColor',
    subType: 'publishedContent.subType',
  } as RequestRecord;

  return Object.keys(filters).reduce(
    (acc, key: string) => ({
      ...acc,
      [(MAPPER[key] as string) || key]: filters[key],
    }),
    {},
  );
};

/**
 * Merge trace headers with existing request headers for distributed tracing
 * Only includes headers that have values to avoid sending empty headers
 * @param existingHeaders - Current request headers
 * @param traceHeaders - Trace headers to add
 * @returns Merged headers object
 */
export const mergeTraceHeaders = (
  existingHeaders: Record<string, string> = {},
  traceHeaders?: TraceHeaders,
): Record<string, string> => {
  if (!traceHeaders) {
    return existingHeaders;
  }

  const filteredTraceHeaders = Object.entries(traceHeaders).reduce(
    (acc, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return {
    ...existingHeaders,
    ...filteredTraceHeaders,
  };
};
